import { Alert, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import * as React from "react";
import { ItemTemplate } from "../../../../shared/items/constants";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { ManageInventoryItem } from "./manageItem";

interface FilesPageStates {
    template: ItemTemplate;
    loading: boolean;
    items: Item[];
}

export interface Item {
    item_id: string;
    created_at: number;
    image: string;
    name: string;
    rarity: string;
    updated_at: number;
    game_id?: number;
    amount?: number;
    category?: string;
    seconds?: number;
    description?: string;
    league?: number;
    leagues?: string;
    group?: string;
    tokens?: string;
    hqLevel?: number;
    price?: number;
    vip_points?: number;
    order?: number;
    type?: string;
    gear_item_id?: string;
    multiplier?: number;
}

class ItemsExplorerWrapped extends React.Component<any, FilesPageStates> {
    constructor(props) {
        super(props);

        this.state = {
            template: require(`../../../../shared/items/templates/${props.match.params.type}`).default,
            loading: true,
            items: [],
        };

        document.title = Helpers.documentTitle(Helpers.capitalizeFirstLetter(props.match.params.type));
    }

    /**
     * Type heading base class
     *
     * @returns
     * @memberof ItemsExplorerWrapped
     */
    typeHeading() {
        switch (this.state.template.type) {
            case "time_boosters":
                return "Boosters";
            case "boosters":
                return "Speed up";
            default:
                return this.state.template.type;
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.match.params.type !== this.state.template.type) {
            document.title = Helpers.documentTitle(Helpers.capitalizeFirstLetter(nextProps.match.params.type));

            this.setState({ template: require(`../../../../shared/items/templates/${nextProps.match.params.type}`).default }, () => {
                this.fetchItems();
            });
        }
    }

    componentDidMount() {
        this.fetchItems();
    }

    refreshList = () => {
        this.fetchItems();
    };

    /**
     * Get bundle data based on bundle ID
     */
    fetchItems = () => {
        this.setState({ loading: true }, () => {
            axios.get(`${Config.getInstance.apiUrl}/api/items/type/${this.state.template.type}`, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                },
            }).then((result) => {
                this.setState({
                    loading: false,
                    items: result.data.data,
                });
            });
        });
    };

    getLastItemId = () => {
        const sortedListById = this.state.items.sort((a, b) => {
            const agame_id = parseInt(a.game_id as unknown as string, 10) || 0;
            const bgame_id = parseInt(b.game_id as unknown as string, 10) || 0;

            return agame_id - bgame_id;
        });

        const lastItem = sortedListById[sortedListById.length - 1];

        if (!lastItem) {
            return null;
        }

        return lastItem.game_id;
    };

    /**
     * Generate next item id
     *
     * @memberof ItemsExplorerWrapped
     */
    generateNextItemId = () => {
        const latestId = this.getLastItemId();
        if (!latestId) {
            return null;
        }

        return parseInt(latestId as any as string, 10) + 1;
    }

    renderTableContent(items) {
        const columnCount = this.state.template.listData.length + 2;

        if (this.state.loading) {
            return <TableRow key="loading-row">
                <TableCell colSpan={columnCount}>
                    {Helpers.renderSkeletonRows(5, 50)}
                </TableCell>
            </TableRow>
        }

        if (items.length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={columnCount}>
                    <Alert severity="info">No Data</Alert>
                </TableCell>
            </TableRow>
        }

        return items.map(item => (
            <TableRow key={item.game_id} hover={true}>
                <TableCell>{item.game_id}</TableCell>

                {this.state.template.listData.map(listDataItem => (
                    <TableCell key={listDataItem.key}>
                        {Helpers.capitalizeFirstLetter(item[listDataItem.key])}
                    </TableCell>
                ))}

                <TableCell>
                    <ManageInventoryItem
                        template={this.state.template}
                        modalTitle="Edit"
                        buttonText="Edit"
                        buttonIcon={true}
                        refreshParent={() => this.refreshList()}
                        item={item}
                        lastitem_id={null}
                    />
                </TableCell>
            </TableRow>
        ));
    };

    formatTableBody(items) {
        return <Table className="min-table-size">
            <TableHead>
                <TableRow>
                    <TableCell style={{width: 20}}>Id</TableCell>

                    {this.state.template.listData.map(item => (
                        <TableCell key={item.key}>{item.label}</TableCell>
                    ))}

                    <TableCell style={{width: 100}}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {this.renderTableContent(items)}
            </TableBody>
        </Table>;
    };

    render() {
        const {classes} = this.props;

        return <div className="contentPadding">
            <Paper className="root" elevation={0}>
                <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                    <div className={classes.title}>
                        <Typography variant="h6" id="tableTitle">
                            {Helpers.capitalizeFirstLetter(this.typeHeading())}
                        </Typography>
                    </div>
                    <div className={classes.actions}>
                        <div style={{marginLeft: 15, display: "inline-block"}}>
                            <ManageInventoryItem
                                template={this.state.template}
                                buttonText={"Add new"}
                                modalTitle={"Add new"}
                                refreshParent={() => this.refreshList()}
                                lastitem_id={this.generateNextItemId()}
                            />
                        </div>
                    </div>
                </div>

                <div style={{float: "left", width: "100%"}}>
                    {this.formatTableBody(this.state.items)}
                </div>
            </Paper>
        </div>;
    }
}

const styles: any = theme => ({
    title: {
        flex: "0 0 auto",
        float: "left",
    },
    actions: {
        color: theme.palette.text.secondary,
        float: "right",
    },
    tableRow: {
        cursor: "pointer",
    },
});

export const ItemsExplorer = withStyles(styles)(ItemsExplorerWrapped);
